import type UpdateRetentionSettings from '@Application/Accounts/RetentionSettings/Commands/UpdateRetentionSettings';
import formatRetentionBound from '@UI/Pages/Administration/Retention/RetentionSettingsForm/Helpers/formatRetentionBound';
import retentionBounds from '@UI/Pages/Administration/Retention/RetentionSettingsForm/Helpers/retentionBounds';
import type RetentionSettingsView from '@UI/Store/RetentionSettings/Views/RetentionSettingsView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

type RetentionSettingsFormArgs = {
	audioRetentionDays: number | null;
	beneficiaryRetentionMonths: number | null;
};

@ValidatedClass()
class RetentionSettingsFormData extends AbstractValidatedClass<RetentionSettingsFormArgs> {
	@constraint(
		Joi.number()
			.integer()
			.min(retentionBounds.audioDays.minimum)
			.max(retentionBounds.audioDays.maximum)
			.required(),
		() =>
			`La durée de conservation de l'audio doit être comprise entre ${formatRetentionBound(retentionBounds.audioDays.minimum)} et ${formatRetentionBound(retentionBounds.audioDays.maximum)} jours.`,
	)
	public readonly audioRetentionDays: number | null;

	@constraint(
		Joi.number()
			.integer()
			.min(retentionBounds.beneficiaryMonths.minimum)
			.max(retentionBounds.beneficiaryMonths.maximum)
			.required(),
		() =>
			`La durée de conservation des dossiers doit être comprise entre ${formatRetentionBound(retentionBounds.beneficiaryMonths.minimum)} et ${formatRetentionBound(retentionBounds.beneficiaryMonths.maximum)} mois.`,
	)
	public readonly beneficiaryRetentionMonths: number | null;

	constructor(data: RetentionSettingsFormArgs) {
		super(data);
		this.audioRetentionDays = data.audioRetentionDays;
		this.beneficiaryRetentionMonths = data.beneficiaryRetentionMonths;
		Object.freeze(this);
	}

	static fromView(view: RetentionSettingsView): RetentionSettingsFormData {
		return new RetentionSettingsFormData({
			audioRetentionDays: view.audioRetentionDays,
			beneficiaryRetentionMonths: view.beneficiaryRetentionMonths,
		});
	}

	setAudioRetentionDays(audioRetentionDays: number | null): this {
		return this.cloneWith({ audioRetentionDays } as Partial<RetentionSettingsFormArgs>);
	}

	setBeneficiaryRetentionMonths(beneficiaryRetentionMonths: number | null): this {
		return this.cloneWith({ beneficiaryRetentionMonths } as Partial<RetentionSettingsFormArgs>);
	}

	/** True when either duration is shorter than the saved one, which deletes data on the next purge. */
	isReductionOf(view: RetentionSettingsView): boolean {
		return (
			(this.audioRetentionDays ?? Number.POSITIVE_INFINITY) < view.audioRetentionDays ||
			(this.beneficiaryRetentionMonths ?? Number.POSITIVE_INFINITY) < view.beneficiaryRetentionMonths
		);
	}

	/** `null` while a field is empty, which `isFormDataValid` already reports. */
	toCommandData(): Pick<
		UpdateRetentionSettings,
		'audioRetentionDays' | 'beneficiaryRetentionMonths'
	> | null {
		if (this.audioRetentionDays === null || this.beneficiaryRetentionMonths === null) {
			return null;
		}

		return {
			audioRetentionDays: this.audioRetentionDays,
			beneficiaryRetentionMonths: this.beneficiaryRetentionMonths,
		};
	}
}

export default RetentionSettingsFormData;
